import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import {
  getProductBookingList,
  updateDeliveryStatus,
  cancelProduct,
  getCouriers,
  assignCourier,
  schedulePickup,
  downloadLabel,
  downloadInvoice,
} from "../../services/productBookingListService";

/* =====================================
   FETCH PRODUCT BOOKING LIST
===================================== */
export const fetchProductBookingList = createAsyncThunk(
  "productBookingList/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      return await getProductBookingList(params);
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch product booking list";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   FETCH COURIERS
===================================== */
export const fetchCouriersThunk = createAsyncThunk(
  "productBookingList/fetchCouriers",
  async (transactionId, { rejectWithValue }) => {
    try {
      return await getCouriers(transactionId);
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch couriers";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   ASSIGN COURIER
===================================== */
export const assignCourierThunk = createAsyncThunk(
  "productBookingList/assignCourier",
  async ({ transactionId, productIds, courierId }, { rejectWithValue }) => {
    try {
      return await assignCourier({
        transactionId,
        productIds,
        courierId,
      });
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to assign courier";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   SCHEDULE PICKUP
===================================== */
export const schedulePickupThunk = createAsyncThunk(
  "productBookingList/schedulePickup",
  async ({ transactionId, productIds }, { rejectWithValue }) => {
    try {
      return await schedulePickup({
        transactionId,
        productIds,
      });
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to schedule pickup";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   DOWNLOAD LABEL
===================================== */
export const downloadLabelThunk = createAsyncThunk(
  "productBookingList/downloadLabel",
  async ({ transactionId, productIds }, { rejectWithValue }) => {
    try {
      const response = await downloadLabel({
        transactionId,
        productIds,
      });

      if (response.success && response.labelUrl) {
        const link = document.createElement("a");
        link.href = response.labelUrl;
        link.setAttribute("download", "shipping_label.pdf");
        link.target = "_blank"; // fallback
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        return response;
      } else {
        throw new Error("Label URL not found in response");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to download label";
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);


/* =====================================
   DOWNLOAD INVOICE
===================================== */
export const downloadInvoiceThunk = createAsyncThunk(
  "productBookingList/downloadInvoice",
  async ({ transactionId, productIds }, { rejectWithValue }) => {
    try {
      const response = await downloadInvoice({
        transactionId,
        productIds,
      });

      // Backend returns JSON with invoiceUrl
      if (response.success && response.invoiceUrl) {
        // Fetch the PDF as blob to force download
        const pdfResponse = await fetch(response.invoiceUrl);
        const blob = await pdfResponse.blob();
        
        // Create blob URL and trigger download
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = `invoice_${transactionId}_${Date.now()}.pdf`;
        link.style.display = "none";
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(blobUrl);
        }, 100);

        return response;
      } else {
        throw new Error("Invoice URL not found in response");
      }
    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to download invoice";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   UPDATE DELIVERY STATUS
===================================== */
export const updateDeliveryThunk = createAsyncThunk(
  "productBookingList/updateDelivery",
  async ({ transactionId, payload }, { rejectWithValue }) => {
    try {
      // payload already contains the 'product' array from the modal
      return await updateDeliveryStatus({
        transactionId,
        payload,
      });
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update delivery";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   CANCEL PRODUCT
===================================== */
export const cancelProductThunk = createAsyncThunk(
  "productBookingList/cancelProduct",
  async ({ transactionId, product, reason }, { rejectWithValue }) => {
    try {
      // product is now an array: [id] or [id1, id2, id3]
      return await cancelProduct({
        transactionId,
        product, // Pass array of product IDs
        reason,
      });
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to cancel product";
      toast.error(message);
      return rejectWithValue(message);
    }
  },
);

/* =====================================
   INITIAL STATE
===================================== */
const initialState = {
  bookings: [],
  total: 0,
  page: 1,
  status: "idle", // loading | succeeded | failed
  actionLoading: false, // for update / cancel
  labelLoading: false, // for label download
  invoiceLoading: false, // for invoice download
  error: null,
  couriers: [], // Available couriers list
  courierLoading: false, // Loading state for couriers
};

/* =====================================
   SLICE
===================================== */
const productBookingListSlice = createSlice({
  name: "productBookingList",
  initialState,
  reducers: {
    resetProductBookingState: () => initialState,
  },
  extraReducers: (builder) => {
    builder

      /* ===== FETCH LIST ===== */
      .addCase(fetchProductBookingList.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchProductBookingList.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.bookings = action.payload.data || [];
        state.total = action.payload.pagination?.total || 0;
        state.page = action.payload.pagination?.page || 1;
      })
      .addCase(fetchProductBookingList.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload;
        state.bookings = [];
      })

      /* ===== FETCH COURIERS ===== */
      .addCase(fetchCouriersThunk.pending, (state) => {
        state.courierLoading = true;
      })
      .addCase(fetchCouriersThunk.fulfilled, (state, action) => {
        state.courierLoading = false;
        state.couriers = action.payload.data || [];
      })
      .addCase(fetchCouriersThunk.rejected, (state) => {
        state.courierLoading = false;
        state.couriers = [];
      })

      /* ===== ASSIGN COURIER ===== */
      .addCase(assignCourierThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(assignCourierThunk.fulfilled, (state) => {
        state.actionLoading = false;
        toast.success("Courier assigned successfully");
      })
      .addCase(assignCourierThunk.rejected, (state) => {
        state.actionLoading = false;
      })

      /* ===== SCHEDULE PICKUP ===== */
      .addCase(schedulePickupThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(schedulePickupThunk.fulfilled, (state) => {
        state.actionLoading = false;
        toast.success("Pickup scheduled successfully");
      })
      .addCase(schedulePickupThunk.rejected, (state) => {
        state.actionLoading = false;
      })

      /* ===== DOWNLOAD LABEL ===== */
      .addCase(downloadLabelThunk.pending, (state) => {
        state.labelLoading = true;
      })
      .addCase(downloadLabelThunk.fulfilled, (state) => {
        state.labelLoading = false;
        toast.success("Label downloaded successfully");
      })
      .addCase(downloadLabelThunk.rejected, (state) => {
        state.labelLoading = false;
      })

      /* ===== DOWNLOAD INVOICE ===== */
      .addCase(downloadInvoiceThunk.pending, (state) => {
        state.invoiceLoading = true;
      })
      .addCase(downloadInvoiceThunk.fulfilled, (state) => {
        state.invoiceLoading = false;
        toast.success("Invoice downloaded successfully");
      })
      .addCase(downloadInvoiceThunk.rejected, (state) => {
        state.invoiceLoading = false;
      })

      /* ===== UPDATE DELIVERY ===== */
      .addCase(updateDeliveryThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateDeliveryThunk.fulfilled, (state) => {
        state.actionLoading = false;
        toast.success("Delivery status updated successfully");
      })
      .addCase(updateDeliveryThunk.rejected, (state) => {
        state.actionLoading = false;
      })

      /* ===== CANCEL PRODUCT ===== */
      .addCase(cancelProductThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelProductThunk.fulfilled, (state) => {
        state.actionLoading = false;
        toast.success("Product cancelled successfully");
      })
      .addCase(cancelProductThunk.rejected, (state) => {
        state.actionLoading = false;
      });
  },
});

export const { resetProductBookingState } = productBookingListSlice.actions;

export default productBookingListSlice.reducer;